<template>
    <NuxtLink
        :to="profileLink"
        class="MentorCard block h-full overflow-hidden rounded-lg bg-white dark:bg-surface-card shadow transition-shadow hover:shadow-lg"
    >
        <div class="flex h-full flex-col">
            <!-- Mentor image -->
            <div class="relative aspect-[5/4] w-full bg-gray-200 dark:bg-subtle">
                <img
                    v-if="mentor.profileImg"
                    :src="mentor.profileImg"
                    :alt="fullName"
                    class="size-full object-cover"
                    loading="lazy"
                />
                <div v-else class="flex h-full items-center justify-center bg-gradient-to-br from-[#047274]/20 to-[#05989B]/30 dark:from-[#047274]/30 dark:to-[#05989B]/40">
                    <span class="text-5xl font-bold text-[#047274]/60 dark:text-[#05989B]/70">{{ nameInitial }}</span>
                </div>

                <div v-if="badges && badges.length" class="absolute bottom-4 left-4 flex flex-wrap gap-1">
                    <CBadge v-for="(badge, idx) in badges" :key="idx" :type="badge.color">{{ badge.text }}</CBadge>
                </div>
                <div v-else-if="badgeMain" class="absolute inset-x-4 bottom-4">
                    <CBadge :type="badgeMainData.color">{{ badgeMainData.text }}</CBadge>
                </div>
            </div>

            <!-- Content -->
            <div class="flex flex-1 flex-col p-5">
                <!-- Name -->
                <h3 class="mb-2 text-lg font-semibold">{{ fullName }}</h3>

                <!-- Current job -->
                <div v-if="currentJob" class="mb-4 line-clamp-2 text-sm text-gray-600 dark:text-txt-body">{{ currentJob }}</div>

                <!-- Location -->
                <div v-if="location" class="mb-4 flex items-start text-sm text-gray-700 dark:text-txt-body">
                    <IconTextRow icon="location-outline" slim class="-ml-1">
                        {{ location }}
                    </IconTextRow>
                </div>

                <!-- Motivation -->
                <p v-if="motivation" class="mb-3 line-clamp-2 text-sm leading-relaxed text-gray-600 dark:text-txt-body">
                    {{ motivation }}
                </p>

                <!-- Interests/Tags -->
                <div v-if="interests && interests.length" class="mb-3 line-clamp-2">
                    <span
                        v-for="tag in interests.slice(0, tagsMaxCount)"
                        :key="tag"
                        class="font-semibold text-sm text-[#1d191f] dark:text-white leading-5 inline"
                    >
                        <span class="text-[#047274]">#</span>{{ tag }}&nbsp;&nbsp;
                    </span>
                </div>

                <!-- CTA button -->
                <div v-if="!hideButton" class="mt-auto pt-3">
                    <NuxtLink
                        :to="profileLink"
                        :target="inNewTab ? '_blank' : '_self'"
                        class="inline-flex items-center font-medium text-primary-500 hover:text-primary-600 hover:dark:text-primary-300"
                    >
                        {{ buttonText }}
                        <CIcon name="chevron-right" class="ml-1 size-4" />
                    </NuxtLink>
                </div>
            </div>
        </div>
    </NuxtLink>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import orderBy from 'lodash-es/orderBy'
import { BadgeMainType } from '#shared/types/Badge'
import type { UserProfile } from '#shared/types/User'
import { getFullName } from '#shared/utils'
import IconTextRow from '@/components/common/IconTextRow.vue'
import { useUser } from '@/composables/user/useUser'
import CIcon from '@/components/ui/CIcon.vue'
import CBadge from '@/components/ui/CBadge.vue'

interface Props {
    mentor: UserProfile
    returnRoute?: Record<string, any>
    searchTagRouteName?: string
    searchTagQueryParam?: string
    loading?: boolean
    badgeMain?: boolean
    preview?: boolean
    inNewTab?: boolean
    tagsMaxCount?: number
    buttonText?: string
    linkTo?: Record<string, any>
    hideButton?: boolean
    badges?: { text: string; color: string }[]
}

const props = withDefaults(defineProps<Props>(), {
    returnRoute: () => ({}),
    searchTagRouteName: 'mentori',
    searchTagQueryParam: 'interests',
    loading: false,
    badgeMain: false,
    preview: false,
    inNewTab: false,
    tagsMaxCount: 5,
    buttonText: 'Viac o mne',
    linkTo: undefined,
    hideButton: false,
    badges: undefined
})

const profileLink = computed(() => {
    if (props.linkTo) return props.linkTo
    return { name: 'mentor-id', params: { id: props.mentor.slug || props.mentor.id } }
})

const { formatLocation } = useUser()

const badgeMainData = computed<BadgeMainType>(() => ({
    text: 'Mentoring',
    color: 'secondary'
}))

const fullName = computed(() => getFullName(props.mentor))

const nameInitial = computed(() => props.mentor?.firstName?.charAt(0) || '?')

const currentJob = computed(() => {
    const currentJob = orderBy(props.mentor?.jobs ?? [], 'endYear', 'desc')[0]
    return [currentJob?.position, currentJob?.org].filter(Boolean).join(' @ ')
})

const interests = computed(() => {
    // Typesense flat field or nested profile
    const fromTypesense = (props.mentor as any)?.mentorInterests
    if (Array.isArray(fromTypesense) && fromTypesense.length > 0) return fromTypesense
    return props.mentor?.mentorProfile?.interests ?? []
})

const motivation = computed(() => {
    return (props.mentor as any)?.mentorMotivation || props.mentor?.mentorProfile?.motivation || ''
})

const location = computed(() => formatLocation(props.mentor))
</script>